import { useScreenHelp } from "./ScreenHelp";
import { appAlert, appConfirm } from "./appDialogs";
import CqcWorkspace from "./CqcWorkspace";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
import { isCareViewAllowed } from "./careEdition";
import InspectorLauncher from "./InspectorLauncher";
// add this import near the top with the others
import StaffDev from './staff_dev';
import './ai-modal.css';
// add this import near the top with the others
import RouteAnalysis from "./route_analysis";
import Services from "./services"; // <-- ADDED
import SMSQueue from "./SMSQueue";
import PwaDev from "./pwa_dev";
import SOPUpload from "./SOPUpload"; // <-- ADDED: SOP Upload & Cascade

import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import Login from "./Login";
import QMap from "./QMap";
import StockFinancials from "./stock_financials";
import StockGraphs from "./stock_graphs";
import Mockup from "./Mockupp";
import StockExcess from "./stock_excess";
import WarehouseView from "./warehouse_view";
import StockInvoices from "./stock_invoices";
import ShelfPictorialView from "./shelfPictoriallView";
import Pickups from "./pickups";
import Settings from "./settings";
import Shelves from "./shelves";
import Updates from './updates';
import Stafflogin from './stafflogin';
import Sms from './sms';
import SmsProd from './smsprod';
import Sitedetails from './sitedetails';
import DriverRoute from "./driverroute";
import ApptsAnalysis from "./appts_analysis"; // <-- added
import Epos from "./epos"; // <-- added
import Nms from "./nms"; // <-- added
import HeadOffice from "./HeadOffice"; // <-- added: fleet dashboard
import SinglePharmacy from "./SinglePharmacy";
import MessageConfiguration from "./MessageConfiguration"; // per-branch shelf message editor
import SingleStaff from "./SingleStaff"; // <-- added: one staff member's whole file
import BusinessInsights from "./BusinessInsights";
import DashboardPanels from "./DashboardPanels";
import ClinicalRecord from "./ClinicalRecord";
import OrgSettings from "./OrgSettings"; // <-- added: single-pharmacy view
import Communications from "./Communications"; // <-- added: staff/manager push notification settings
import Navigator from "./Navigator"; // <-- added: floating "where am I / how do I" panel
import GuidedTour from "./GuidedTour"; // <-- added: interactive spotlight walkaround
import Staff from "./Staff"; // <-- added: staff roster + appraisals
import HolidayRota from "./holiday_rota"; // <-- added: per-branch holiday/absence rota
import EstateInspection from "./estate_inspection"; // <-- added: estate-wide inspection snapshot
import DeliveryActivity from "./DeliveryActivity"; // <-- added: delivery activity bar chart
import Payments from "./Payments"; // <-- added: delivery payment plans by branch
import DeliveryHub from "./DeliveryHub"; // Deliveries page: route analysis, activity and payments as tabs
import Workforce from "./Workforce"; // <-- added: hours per 1000 items grid
import Inspection from "./Inspection"; // <-- added: branch inspection workbench
import Config from "./Config"; // <-- added: configurables admin
import RegulatoryCompliance from "./RegulatoryCompliance"; // <-- added: regulatory compliance dashboard
import CqcSites from "./CqcSites"; // <-- added: CQC compliance dashboard (sister to Regulatory Compliance)
import CqcWorklist from "./CqcWorklist"; // <-- added: CQC Worklist — flat cross-site "what's outstanding" view
import CqcInspectorView from "./CqcInspectorView"; // <-- added: Admin > CQC Inspector view (Pharmace all only)
import CqcContributorTask from "./CqcContributorTask"; // <-- added: public "?cqcTask=TOKEN" contributor portal (no login)
import PatientFeedbackPublic from "./PatientFeedbackPublic"; // <-- added: public "?pfb=TOKEN" patient feedback page (no login)
import FeedbackOutcomePublic from "./FeedbackOutcomePublic"; // <-- added: public "?fbo=TOKEN" complaint outcome-recording page (no login)
import InspectorSummaryPublic from "./InspectorSummaryPublic"; // <-- added: public "?insp=TOKEN" inspector summary page (no login, PIN-gated)
import FeedbackRatingsPublic from "./FeedbackRatingsPublic"; // <-- added: public "?pfbr=TOKEN&view=org|branches" star ratings dashboard (no login)
import SiteManagement from "./SiteManagement"; // <-- added: Admin > Site management (Pharmace all only)
import InvoiceProcessing from "./invoice_processing"; // <-- added: Admin > Invoice processing (Pharmace all only)
import PharmacistRota from "./PharmacistRota"; // <-- added: pharmacist cover rota (beta)

import QuestionBox from "./QuestionBox";
import "./App.css";
import pharmaceLogo from "./assets/logo.png";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
const App = () => {
  // ---------------------------------------------------------------------------
  // Read-only single-pharmacy viewing link: /?spview=TOKEN (minted by the
  // "Create viewing URL" button on the Single pharmacy page). The token is the
  // credential — no login, no nav rail, one pharmacy, view-only.
  // NOTE: this early return runs before ANY hook, so hook order stays stable.
  // ---------------------------------------------------------------------------
  const spviewToken = new URLSearchParams(window.location.search).get("spview");
  if (spviewToken) {
    return <LocalizationProvider dateAdapter={AdapterDayjs}>
        <SinglePharmacy viewToken={spviewToken} />
      </LocalizationProvider>;
  }

  // ---------------------------------------------------------------------------
  // CQC contributor-task link: /?cqcTask=TOKEN (emailed from the "Ask a
  // contributor" panel in CqcEvidenceDialog.js). Same shape as ?spview=TOKEN
  // above — the token is the credential, no login, no nav rail, scoped to
  // exactly one evidence element. NOTE: this early return runs before ANY
  // hook, so hook order stays stable.
  // ---------------------------------------------------------------------------
  const cqcTaskToken = new URLSearchParams(window.location.search).get("cqcTask");
  if (cqcTaskToken) {
    return <CqcContributorTask token={cqcTaskToken} />;
  }

  // ---------------------------------------------------------------------------
  // Patient feedback link: /?pfb=TOKEN (the QR code on a branch's "Surveys >
  // Feedback" poster, minted by feedback_routes.js). Same shape as the two
  // early returns above — the token is the credential, no login, no nav
  // rail, scoped to exactly the one branch it was minted for. NOTE: this
  // early return runs before ANY hook, so hook order stays stable.
  // ---------------------------------------------------------------------------
  const pfbToken = new URLSearchParams(window.location.search).get("pfb");
  if (pfbToken) {
    return <PatientFeedbackPublic token={pfbToken} />;
  }

  // ---------------------------------------------------------------------------
  // Complaint outcome-recording link: /?fbo=TOKEN (the "Click here to record
  // outcome of complaint" link in the branch manager's complaint-copy email
  // and every later reminder email — see feedback_routes.js). Same shape as
  // the other early returns above — the token is the credential, no login,
  // no nav rail, scoped to exactly the one complaint it was minted for.
  // NOTE: this early return runs before ANY hook, so hook order stays stable.
  // ---------------------------------------------------------------------------
  const fboToken = new URLSearchParams(window.location.search).get("fbo");
  if (fboToken) {
    return <FeedbackOutcomePublic token={fboToken} />;
  }

  // ---------------------------------------------------------------------------
  // Inspector summary link: /?insp=TOKEN (minted by "Email inspector summary"
  // on the Single pharmacy page — see inspector_summary_routes.js). Same shape
  // as the three early returns above — the token is the credential (plus a
  // 6-digit PIN checked inside InspectorSummaryPublic), no login, no nav rail.
  // NOTE: this early return runs before ANY hook, so hook order stays stable.
  // ---------------------------------------------------------------------------
  const inspToken = new URLSearchParams(window.location.search).get("insp");
  if (inspToken) {
    return <InspectorSummaryPublic token={inspToken} />;
  }

  // ---------------------------------------------------------------------------
  // Star ratings dashboard link: /?pfbr=TOKEN&view=org|branches (minted by the
  // "Star ratings" tab of Surveys > Feedback — see PatientFeedback.js /
  // feedback_routes.js). Same shape as the other early returns above — the
  // token is the credential, no login, no nav rail. One token serves both
  // copyable URLs; ?view= just picks which graphical breakdown to show.
  // NOTE: this early return runs before ANY hook, so hook order stays stable.
  // ---------------------------------------------------------------------------
  const pfbrToken = new URLSearchParams(window.location.search).get("pfbr");
  if (pfbrToken) {
    const view = new URLSearchParams(window.location.search).get("view") === "branches" ? "branches" : "org";
    return <FeedbackRatingsPublic token={pfbrToken} view={view} />;
  }
  return <AuthedApp />;
};
const AuthedApp = () => {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [userData, setUserData] = useState(null);
  // Business Insights is under construction: an amber nav button that shows
  // a closable notice each time it is opened.
  const [biNotice, setBiNotice] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [currentView, setCurrentViewState] = useState("head_office");
  const setCurrentView = useCallback(value => setCurrentViewState(previous => {
    const next = typeof value === "function" ? value(previous) : value;
    return isCareViewAllowed(next) ? next : "head_office";
  }), []);
  const [settingsOpen, setSettingsOpen] = useState(false);
  useScreenHelp(settingsOpen ? { title: "Settings", blurb: "Organisation, site and communication settings for your account.", can: ["Select the settings tab and organisation you want to configure.", "Review the changes, save them if intended, then close Settings to return to your previous screen."], related: [] } : null, 200);
  const [shelvesOpen, setShelvesOpen] = useState(false);
  const [answer, setAnswer] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [loadingAnswer, setLoadingAnswer] = useState(false);
  const [clarificationOptions, setClarificationOptions] = useState(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState("");
  const [selectedMetric, setSelectedMetric] = useState("");
  const [showStockMenu, setShowStockMenu] = useState(false);

  // Unified state for the categorised dropdown menus.
  // Only one dropdown is open at a time. Click-outside closes it.
  const [openMenu, setOpenMenu] = useState(null); // "operations" (Staff) | "stock" | "services" | "admin" | "settings" | null
  const navRef = useRef(null);

  // Pale-grey button style for the left nav rail (beats global blue button rule via inline style)
  // Pharmace app design system: transparent rows, 6px radius, Public Sans.
  const navBtnStyle = {
    backgroundColor: "transparent",
    color: "#5D5D5D",
    border: "1px solid transparent",
    borderRadius: 6,
    padding: "8px 12px",
    fontSize: 14,
    fontFamily: "'Public Sans', system-ui, sans-serif",
    fontWeight: 400,
    lineHeight: 1.35,
    textAlign: "left",
    width: "100%",
    cursor: "pointer",
    transition: "background-color 180ms cubic-bezier(0.4,0,0.2,1), color 180ms cubic-bezier(0.4,0,0.2,1)"
  };

  // ---------------------------------------------------------------------------
  // ACTIVE RAIL HIGHLIGHTING
  // ---------------------------------------------------------------------------
  // Which flyout owns which routes, so a parent ("Stock", "Delivery") lights up
  // when you are inside one of its children. Keys match the openMenu values.
  const NAV_GROUPS = {
    delivery: ["route_analysis", "delivery_activity", "payments"],
    stock: ["stock_financials", "shelfPictorialView", "stock_excess", "stock_graphs", "stock_invoices", "warehouse_view"],
    services: ["services", "nms"],
    admin: ["qmap", "pickups", "smsprod", "sms", "staff_dev", "pwa_dev", "sms_queue", "sitedetails", "site_management", "invoice_processing", "cqc_inspector"]
  };

  // navBtn(view, extra) — the rail style with an active variant.
  //
  // The active look is a thick teal left edge plus a teal border, NOT a
  // background swap. Several rail buttons carry a meaningful colour of their
  // own (Regulatory Compliance is blue, Custom panels amber); replacing the
  // background to show selection would destroy that signal. The left edge sits
  // on top of any colour, so both survive. paddingLeft drops by the extra
  // border width so the label doesn't jump sideways when selected.
  const navBtn = (view, extra = {}) => {
    // `view` may be a route name, a list of route names, or a plain boolean —
    // the last for buttons that open a dialog rather than change currentView
    // (Settings, Stock Shelves), which are just as much "where you are".
    const on = typeof view === "boolean" ? view : Array.isArray(view) ? view.includes(currentView) : currentView === view;
    if (!on) return {
      ...navBtnStyle,
      ...extra
    };
    return {
      ...navBtnStyle,
      ...extra,
      fontWeight: 600,
      color: extra.color || "#0E8CFF",
      backgroundColor: extra.backgroundColor || extra.background || "#E6F7FF",
      boxShadow: "inset 3px 0 0 #0E8CFF"
    };
  };

  // Same treatment for a flyout parent whose child route is the current view.
  const navGroupBtn = (groupKey, extra = {}) => navBtn(NAV_GROUPS[groupKey] || [], extra);

  // Navigator — the floating "where am I / how do I" panel at the foot of the
  // rail. Kept in App because it needs currentView and goTo, and because it must
  // outlive any single view: it stays open while you follow its instructions.
  const [navigatorOpen, setNavigatorOpen] = useState(false);

  // Guided tour \u2014 a real, in-app spotlight walkaround (GuidedTour.js), as
  // opposed to Navigator's static reference panel above. Auto-launches once
  // per browser on first login (TOUR_SEEN_LS unset), and is always available
  // again afterwards from "Take the tour" at the foot of the rail.
  const TOUR_SEEN_LS = "tourSeen";
  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => {
    if (!token) return;
    if (localStorage.getItem(TOUR_SEEN_LS)) return;
    setTourOpen(true);
  }, [token]);
  const closeTour = () => {
    try {
      localStorage.setItem(TOUR_SEEN_LS, "1");
    } catch (_) {}
    setTourOpen(false);
  };
  useEffect(() => {
    function handleClickOutside(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper: choose a view and close any open dropdown.
  // navEpoch increments on EVERY rail click and keys the rendered view, so
  // clicking a menu button always lands fresh at that section's top level —
  // including re-clicking the section you are already in (previously a
  // silent no-op: setCurrentView with the same value doesn't re-render, so
  // deep drill-down state inside the section appeared "stuck").
  const [navParams, setNavParams] = useState(null);
  const [navEpoch, setNavEpoch] = useState(0);
  const goTo = (view, params = null) => {
    setCurrentView(view);
    setNavParams(params);
    setNavEpoch(e => e + 1);
    setOpenMenu(null);
  };

  // Pagination & Sorting & Filtering
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const [sortKey, setSortKey] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [question, setQuestion] = useState("");
  const handleShelvesClick = () => setShelvesOpen(true);
  const handleSettingsClick = () => setSettingsOpen(true);

  /* ---------------------------------------------------------------------------
     CLINICAL RECORD - optional module.
      The nav button is hidden unless the organisation has switched the module on
     in Settings -> Organisation Settings -> Modules. The flag is the group
     setting `clinical_record`, mirrored into localStorage by Login.js at sign-in
     and by the Settings dialog when it saves.
      It is held in STATE rather than read from localStorage at render time, so
     that saving the setting shows or hides the button straight away instead of
     waiting for the next page load. The value is re-read when the Settings
     dialog closes (see the <Settings /> onClose below).
      Absent means off: an organisation that has never seen this setting gets the
     button hidden, which is how it behaved before.
     --------------------------------------------------------------------------- */
  const CLINICAL_RECORD_LS = "clinical_record";
  const readClinicalRecordEnabled = () => {
    try {
      return JSON.parse(localStorage.getItem(CLINICAL_RECORD_LS) || "false") === true;
    } catch (_) {
      return false;
    }
  };
  const [clinicalRecordVisible, setClinicalRecordVisible] = useState(readClinicalRecordEnabled);

  // Switching the module off while the page is on screen must not leave the
  // user staring at an empty panel, so send them back to the head office view.
  useEffect(() => {
    if (!clinicalRecordVisible && currentView === "clinical_record") {
      setCurrentView("head_office");
    }
  }, [clinicalRecordVisible, currentView]);

  // CQC management is available to signed-in group accounts. All API data
  // remains scoped to the account's organisation claim. The cross-account
  // inspector retains the existing platform-administrator gate.
  /* ---------------------------------------------------------------------------
     "Appraisal embedded" (menu button; Pharmace all + Southtown Group only).
     Opens the standalone embeddable-appraisal page on pharmaceinstall:
       https://pharmaceinstall.co.uk/appraisals.html?key=wk_...
     The full widget key is only returned once at creation (the list endpoint
     returns tails), so the first click creates a dedicated demo key for the
     'Demo Live' pharmacy (origin-locked to pharmaceinstall) and the finished
     link is remembered in this browser (localStorage) for every later click.
     --------------------------------------------------------------------------- */
  const APPRAISAL_DEMO_LS = "appraisal_embedded_demo_url";
  const [appraisalDemoBusy, setAppraisalDemoBusy] = useState(false);
  const appraisalDemoVisible = ["pharmace all", "southtown group"].includes((localStorage.getItem("organisationName") || "").trim().toLowerCase());
  const openAppraisalEmbeddedDemo = async () => {
    const saved = localStorage.getItem(APPRAISAL_DEMO_LS) || "";
    if (saved.startsWith("https://pharmaceinstall.co.uk/appraisals.html?key=wk_")) {
      window.open(saved, "_blank", "noopener");
      return;
    }
    let members = [];
    try {
      members = JSON.parse(localStorage.getItem("members")) || [];
    } catch (_) {}
    // The demo is always keyed to the 'Demo Live' pharmacy; fall back to the
    // first pharmacy only if Demo Live isn't a member of this organisation.
    const demoMember = members.find(m => String(m?.pharmacyName || "").trim().toLowerCase() === "demo live");
    const targetName = demoMember?.pharmacyName || members[0]?.pharmacyName || "";
    if (!targetName) {
      (await appAlert("No pharmacy found on this organisation to demo with."));
      return;
    }
    if (!(await appConfirm(`Open the embedded appraisal demo?\n\nThis creates a demo widget key for "${targetName}"` + (demoMember ? "" : " (no 'Demo Live' pharmacy was found, so the first pharmacy is used)") + ` — locked to pharmaceinstall.co.uk — and opens the standalone appraisal page with it. ` + `The link is remembered in this browser for next time.`))) return;
    setAppraisalDemoBusy(true);
    try {
      const res = await fetch(`${API_ORIGIN}/api/appraisals/widget-keys/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("token")}`
        },
        body: JSON.stringify({
          pharmacyName: targetName,
          label: "Appraisal embedded demo (menu)",
          allowedOrigins: ["https://pharmaceinstall.co.uk"],
          issuedBy: {
            name: localStorage.getItem("userName") || "",
            roleKey: localStorage.getItem("roleKey") || "GROUP_MANAGER"
          }
        })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      const url = `https://pharmaceinstall.co.uk/appraisals.html?key=${d.key}`;
      try {
        localStorage.setItem(APPRAISAL_DEMO_LS, url);
      } catch (_) {}
      window.open(url, "_blank", "noopener");
    } catch (e) {
      (await appAlert(`Could not open the demo: ${e.message}`));
    } finally {
      setAppraisalDemoBusy(false);
    }
  };

  /* ---------------------------------------------------------------------------
     "Insert demo data" (menu button; Southtown Group only). Calls the backend
     demo seeder (POST /api/demoseed/run), which INSERTS artificial:true demo
     records into the four hardcoded test databases, high-water-mark style:
       - continues each staff member's clock history from their last seeded day
       - TODAY gets clock-in-only sessions (staff haven't left yet)
       - any earlier artificial sessions still missing a clock-out are closed
         retrospectively ("orphan cleanup")
     The server re-checks the organisation, so the button is cosmetic gating
     only; nothing outside the four test databases can ever be written.
     --------------------------------------------------------------------------- */
  const [demoSeedBusy, setDemoSeedBusy] = useState(false);
  const demoSeedVisible = (localStorage.getItem("organisationName") || "").trim().toLowerCase() === "southtown group";
  const runDemoSeed = async () => {
    if (!(await appConfirm("Refresh demo data in the four Southtown test sites?\n\n" + "CLOCK IN/OUT HISTORY IS REPLACED. Existing demo clock sessions and break logs for " + "the seeding window are DELETED and regenerated, so a re-run reflects the current " + "generator rather than adding a second batch alongside the first.\n\n" + "Everything else is insert-only and left as it is: staff, roles, schedules, " + "appraisals, pre-appraisals and 12-month reviews. Staff records keep their ids, so " + "appraisal history survives.\n\n" + "Only records flagged artificial:true are ever deleted - real clockings in those " + "test sites are untouched, and days that already hold a real clocking are skipped.\n\n" + "Today's sessions get a clock-in only (staff are still on site).\n\nProceed?"))) return;
    setDemoSeedBusy(true);
    try {
      const res = await fetch(`${API_ORIGIN}/api/demoseed/run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("token")}`
        },
        body: JSON.stringify({
          mode: "seed"
        })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      const t = d.totals || {};
      (await appAlert("Demo data refreshed across the four test sites:\n\n" + `Clock sessions replaced: ${t.attendanceDeleted || 0} deleted, ${t.attendance || 0} regenerated\n` + `  (of which ${t.todayPartials || 0} are today's clock-in-only)\n` + `Break logs: ${t.breaksDeleted || 0} deleted, ${t.breaks || 0} regenerated\n` + (t.attendanceKeptOlder ? `Older than the seeding window, left alone: ${t.attendanceKeptOlder}\n` : "") + `Orphan sessions closed: ${t.orphansClosed || 0}\n\n` + "Left as they were (insert-only):\n" + `Staff created: ${t.staffCreated || 0} | Schedule rows: ${t.timesDocs || 0}\n` + `Appraisals: ${t.appraisals || 0} | Pre-appraisals: ${t.preappraisals || 0} | 12-month reviews: ${t.reviews || 0}\n` + `Holiday requests: ${t.leaveRequests || 0} (${t.leaveApproved || 0} approved, ` + `${t.leaveRejected || 0} rejected, ${t.leavePending || 0} awaiting)\n` + `Absences: ${t.absences || 0} spells over ${t.absenceDays || 0} days\n` + "Only artificial:true records were deleted."));
    } catch (e) {
      (await appAlert(`Demo data insert failed: ${e.message}`));
    } finally {
      setDemoSeedBusy(false);
    }
  };

  /* ---------------------------------------------------------------------------
     "Wipe demo data" — the same endpoint in wipe mode.
     Exists because the CLI cannot be used on this host: the Mongo connection
     strings live inside server.js rather than in an .env the script can read,
     so `node seed_southtown_demo.js --wipe` falls back to localhost and is
     refused. In-process is the only way to reach a wipe.
     Deletes ONLY artificial:true records, and only in the four hardcoded test
     databases — the server re-checks both, so this button is cosmetic gating.
     --------------------------------------------------------------------------- */
  const [demoWipeBusy, setDemoWipeBusy] = useState(false);
  const runDemoWipe = async () => {
    if (!(await appConfirm("Delete ALL demo data from the four Southtown test sites?\n\n" + "Removes artificial:true staff, schedules, clock sessions, break logs, " + "appraisals, pre-appraisals, reviews, holiday requests, absences and " + "goods-in invoices.\n\n" + "Real records are never touched: every delete is filtered on " + "artificial:true, and nothing outside the four test databases is opened.\n\n" + "Invoices raised by hand in the app carry no artificial flag and will " + "SURVIVE this.\n\nProceed?"))) return;
    setDemoWipeBusy(true);
    try {
      const res = await fetch(`${API_ORIGIN}/api/demoseed/run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("token")}`
        },
        body: JSON.stringify({
          mode: "wipe"
        })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      const lines = (d.report || []).map(r => {
        const cols = Object.entries(r).filter(([k, v]) => k !== "dbName" && typeof v === "number" && v > 0).map(([k, v]) => `  ${k}: ${v}`);
        return `${r.dbName}\n${cols.length ? cols.join("\n") : "  nothing to delete"}`;
      });
      (await appAlert("Demo data wiped.\n\n" + lines.join("\n\n") + "\n\nOnly artificial:true records were deleted. " + "Use Insert demo data to regenerate."));
    } catch (e) {
      (await appAlert(`Demo data wipe failed: ${e.message}`));
    } finally {
      setDemoWipeBusy(false);
    }
  };

  /* ---------------------------------------------------------------------------
     "Insert compliance demo" / "Wipe compliance demo" (Southtown Group only).
     Separate from the clock in/out demo above. POST /api/demoseed/compliance/run
     rebuilds 12 months of Regulatory Compliance demo data up to today for the
     four test sites; /wipe removes it. Every record is flagged artificial:true
     with demoSet "compliance", and the server re-checks the organisation.
     --------------------------------------------------------------------------- */
  const [complianceDemoBusy, setComplianceDemoBusy] = useState("");
  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("token")}`
  });
  const runComplianceDemo = async () => {
    if (!(await appConfirm("Build the Regulatory Compliance demo for the four Southtown test sites?\n\n" + "This creates 12 months of demo records up to today: fridge readings, cleaning, date checking, " + "CD registers, RP sign-ins, incidents, MHRA responses, SOPs, NMS, monthly summaries, and a " + "Working grid cell for every item with photos and documents. Talha Test keeps its RP record, " + "cleaning, near-miss and complaints logs in spreadsheets, which are imported and analysed.\n\n" + "ANY PREVIOUS COMPLIANCE DEMO IS REMOVED FIRST, and your trial Working grid cells and row " + "settings for these four sites are OVERWRITTEN (no backup).\n\n" + "No tasks or emails are sent for demo items. Real records are never changed.\n\n" + "It takes a minute or two. Proceed?"))) return;
    setComplianceDemoBusy("seed");
    try {
      const res = await fetch(`${API_ORIGIN}/api/demoseed/compliance/run`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          mode: "seed"
        })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      const lines = (d.perSite || []).map(s => {
        const c = s.counts || {};
        return `${s.pharmacyName} (${s.profile}): ${c["working grid cells"] || 0} grid cells, ${c.photos || 0} photos, ` + `${c["documents (PDF)"] || 0} documents, ${c["fridge readings"] || 0} fridge readings` + (c["spreadsheets imported"] ? `\n   imported: ${c["spreadsheets imported"]}` : "");
      });
      (await appAlert("Compliance demo built.\n\n" + lines.join("\n") + `\n\nTrial Working grid cells replaced: ${d.trialCellsReplaced || 0}.` + "\nOpen Regulatory Compliance and reload to see it."));
    } catch (e) {
      (await appAlert(`Compliance demo failed: ${e.message}`));
    } finally {
      setComplianceDemoBusy("");
    }
  };
  const runComplianceDemoWipe = async () => {
    if (!(await appConfirm("Remove the Regulatory Compliance demo from the four Southtown test sites?\n\n" + "Deletes only records flagged as compliance demo data. The clock in/out demo, real records " + "and other organisations are not touched. The Working grid cells the demo filled are left " + "empty (your earlier trial settings are not restored).\n\nProceed?"))) return;
    setComplianceDemoBusy("wipe");
    try {
      const res = await fetch(`${API_ORIGIN}/api/demoseed/compliance/wipe`, {
        method: "POST",
        headers: authHeaders(),
        body: "{}"
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      const total = Object.values(d.wiped || {}).reduce((t, r) => t + Object.values(r || {}).reduce((a, v) => a + (typeof v === "number" ? v : 0), 0), 0);
      (await appAlert(`Compliance demo removed: ${total} records deleted.`));
    } catch (e) {
      (await appAlert(`Compliance demo wipe failed: ${e.message}`));
    } finally {
      setComplianceDemoBusy("");
    }
  };
  const handleLogout = () => {
    localStorage.removeItem("token");
    setToken(null);
  };
  const handleAskQuestion = async question => {
    if (!question.trim()) return;
    setQuestion(question);
    setLoadingAnswer(true);
    setClarificationOptions(null);
    setAnswer("");
    setCurrentPage(1);
    const members = JSON.parse(localStorage.getItem("members") || "[]");
    try {
      const res = await fetch(apiUrl("/aiquery"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          question,
          members
        })
      });
      if (!res.ok) {
        console.error("Server returned an error status:", res.status);
        setAnswer(`Server error: ${res.status}`);
        setShowDialog(true);
        return;
      }
      const data = await res.json();
      if (data.needClarification) {
        setClarificationOptions({
          message: data.message,
          missingFields: data.missingFields
        });
        return;
      }
      setAnswer(data.answer || "No response received.");
      setShowDialog(true);
      await fetch(apiUrl("/savequestion"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          groupId: localStorage.getItem("userId"),
          question
        })
      });
    } catch (error) {
      console.error("Error during /aiquery fetch:", error);
      setAnswer("An error occurred while fetching data.");
      setShowDialog(true);
    } finally {
      setLoadingAnswer(false);
    }
  };
  const handleClarifiedQuery = async () => {
    if (!selectedPharmacy || !selectedMetric) return;
    const newQuestion = `What is the ${selectedMetric} at ${selectedPharmacy}?`;
    await handleAskQuestion(newQuestion);
  };
  const handleExportToExcel = () => {
    if (!Array.isArray(answer) || answer.length === 0) return;
    const exportData = answer.map(item => {
      const filtered = {};
      for (const [key, value] of Object.entries(item)) {
        if (["_id", "log"].includes(key)) continue;
        if (["price", "value", "totalPrice", "totalValue"].includes(key) && typeof value === "number") {
          filtered[key] = (value / 100).toFixed(2);
        } else {
          filtered[key] = value;
        }
      }
      return filtered;
    });
    import("xlsx").then(xlsx => {
      const workbook = xlsx.utils.book_new();
      const worksheet = {};
      const groupName = localStorage.getItem("organisationName") || "Group";
      const now = new Date();
      const formatted = now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
      const questionPrompt = question || "";
      const headerRows = [[groupName], [`Generated at: ${formatted}`], [`Prompt: ${questionPrompt}`], []];
      xlsx.utils.sheet_add_aoa(worksheet, headerRows, {
        origin: "A1"
      });
      const headers = Object.keys(exportData[0] || {});
      xlsx.utils.sheet_add_aoa(worksheet, [headers], {
        origin: "A5"
      });
      xlsx.utils.sheet_add_json(worksheet, exportData, {
        origin: "A6",
        skipHeader: true
      });
      const allRows = [...headerRows, headers, ...exportData.map(obj => headers.map(k => obj[k] ?? ""))];
      worksheet["!cols"] = headers.map((_, colIdx) => {
        const maxLength = allRows.reduce((max, row) => {
          const val = row[colIdx];
          const len = val ? String(val).length : 0;
          return Math.max(max, len);
        }, 10);
        return {
          wch: maxLength + 2
        };
      });
      xlsx.utils.book_append_sheet(workbook, worksheet, "AI_Results");
      xlsx.writeFile(workbook, "AI_Response.xlsx");
    });
  };
  const filteredAndSorted = useMemo(() => {
    if (!Array.isArray(answer)) return [];
    let result = [...answer];
    if (filterText) {
      result = result.filter(row => Object.values(row).some(val => String(val).toLowerCase().includes(filterText.toLowerCase())));
    }
    if (sortKey) {
      result.sort((a, b) => {
        const valA = a[sortKey];
        const valB = b[sortKey];
        if (valA == null) return 1;
        if (valB == null) return -1;
        if (typeof valA === "number" && typeof valB === "number") {
          return sortAsc ? valA - valB : valB - valA;
        }
        return sortAsc ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
      });
    }
    return result;
  }, [answer, filterText, sortKey, sortAsc]);
  const totalPages = Math.ceil(filteredAndSorted.length / pageSize);
  const paginatedAnswer = filteredAndSorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // ---- wall display -------------------------------------------------------
  // ?wall=<dashboardId> renders one dashboard and nothing else: no menu, no
  // header, no chrome. This is for a screen on the wall, so anything that is
  // not the panels is wasted pixels - and there is nothing to click by
  // accident. The token still comes from localStorage, which is shared across
  // tabs on the same origin, so the new tab is already signed in.
  const wallDash = new URLSearchParams(window.location.search).get("wall");
  if (wallDash && token) {
    return <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DashboardPanels wallDashboardId={wallDash} />
      </LocalizationProvider>;
  }
  return <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="app-container">
        {!token ? <Login setToken={setToken} setUserData={setUserData} /> : <>
            <div style={{
          display: "flex",
          alignItems: "center",
          gap: "15px",
          marginTop: "4px",
          marginBottom: "4px",
          flexWrap: "wrap"
        }}>
              <div style={{
            display: "flex",
            alignItems: "center",
            gap: 10
          }}>
                <img src={pharmaceLogo} alt="Pharmace" style={{
              height: 30,
              width: "auto",
              display: "block"
            }} />
                <h2 className="group-title" style={{
              margin: 0,
              whiteSpace: "nowrap"
            }}>
                  CQC Toolkit - {localStorage.getItem("organisationName") || "Group"}
                </h2>
              </div>
              {/* Section-header slot: section components (Head Office etc.)
                  portal their own heading + toolbars in here so the page
                  title sits ON this line instead of burning a row below. */}
              <div id="section-header-slot" style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flex: 1,
            minWidth: 0,
            flexWrap: "wrap"
          }} />
              {/* Hidden per request (kept in code — restore by removing this
                  comment wrapper): the ask-me-a-question bar, its answer
                  button and the Suggestions toggle all live inside
                  QuestionBox.
               <QuestionBox onAsk={handleAskQuestion} />
               */}
            </div>

            {/* ── Layout split: left navigation rail + right content area ── */}
            <style>{`
              .nav-rail button {
                background-color: transparent;
                color: #5D5D5D;
                border: 1px solid transparent;
                border-radius: 6px;
                padding: 8px 12px;
                font-family: 'Public Sans', system-ui, sans-serif;
                font-size: 14px;
                cursor: pointer;
              }
              .nav-rail button:hover {
                background-color: #FAFAFA !important;
                color: #262626 !important;
              }
            `}</style>
            <div style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "15px"
        }}>
              {/* Left nav rail */}
              <div ref={navRef} className="nav-rail" style={{
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            flexShrink: 0,
            width: 180,
            padding: "12px 8px",
            background: "#fff",
            border: "1px solid #F0F0F0",
            borderRadius: 8,
            boxShadow: "0 8px 25px rgba(0,0,0,0.05)",
            position: "sticky",
            top: 10,
            alignSelf: "flex-start"
          }}>
                {/* Head Office — fleet dashboard for /api/headoffice/* — default landing page */}
                <button onClick={() => goTo("head_office")} style={navBtn("head_office")} data-tour="nav-head_office" title="A live 'how am I driving?' dashboard — current, real-time status across the whole fleet at a glance.">Head Office</button>

                {/* Site Activity — most-used, kept standalone */}
                

                {/* Single pharmacy — Head-Office-style view scoped to one pharmacy */}
                <button onClick={() => goTo("single_pharmacy")} style={navBtn("single_pharmacy")} title="The Head Office dashboard scoped to one branch — shows the live status of a single pharmacy at a time.">Single pharmacy</button>

                {/* Message configuration button removed (2026-10): it is now a tab
                    on Head Office → Messaging (click the Messaging card). */}

                {/* Single staff — the same idea scoped to one person */}
                <button onClick={() => goTo("single_staff")} style={navBtn("single_staff")} title="Everything held about one staff member — appraisals, documents, SOP sign-off and training — in one place. Includes leavers.">Single staff</button>

                {/* Inspection menu button removed per request (2026-07). The
                    "inspection" route and the Inspection component remain
                    intact — restore this block to bring the menu entry back.
                 <button onClick={() => goTo("inspection")} style={{ ...navBtnStyle, backgroundColor: "#FFF7E6" }}
                  title="Inspection-readiness workbench — work through a branch's evidence and readiness ahead of a GPhC inspection.">Inspection</button>
                 */}

                {/* Regulatory Compliance — fleet compliance against GPhC + statutory duties */}
                

                <button onClick={() => goTo("care_inspector")} style={navBtn("care_inspector")} title="Read-only care-compliance evidence for your organisations">Inspector view</button>

                <button onClick={() => goTo("cqc_compliance")} style={navBtn("cqc_compliance", {
              backgroundColor: "#F9F0FF"
            })} title="Selected organisations, service profiles and cumulative CQC evidence readiness">Care compliance (UK)</button>
                <button onClick={() => goTo("cqc_worklist")} style={navBtn("cqc_worklist", {
              backgroundColor: "#F9F0FF"
            })} title="Required evidence from each organisation’s reviewed service profile">Care worksheet</button>

                {/* Deliveries button removed (2026-10): Route analysis, Activity and
                    Payments are now tabs on Head Office → Deliveries. The views
                    below stay so a pharmacy's "route analysis" link still works. */}

                {/* Staff flyout removed per request — Workforce planner,
                    Staff clocking in/out (Raw data) and Pharmacist rota now
                    live as tabs inside Head Office -> Staff -> Clock in/out.
                    The components and their currentView routes below remain
                    intact; restore this block to bring the menu back.
                 {openMenu === "operations" && null}
                 */}

                {/* Stock */}
                

                {/* Services */}
                

                {/* Admin — gated by Pharmace all */}
                {(localStorage.getItem("organisationName") || "").trim() === "Pharmace all" && <div>
                    <button onClick={() => setOpenMenu(openMenu === "admin" ? null : "admin")} style={navGroupBtn("admin")} title="Developer/admin tools (Pharmace all only) — internal utilities.">
                      Admin {openMenu === "admin" ? "▼" : "▶"}
                    </button>
                    {openMenu === "admin" && <div style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                marginLeft: 12,
                marginTop: 4
              }}>
                        <button onClick={() => goTo("qmap")} style={navBtn("qmap")} title="A map view of all branches and related locations.">Map</button>
                        <button onClick={() => goTo("pickups")} style={navBtn("pickups")} title="Collection times — when prescriptions are being collected across branches.">Collection times</button>
                        <button onClick={() => goTo("smsprod")} style={navBtn("smsprod")} title="SMS history — messages sent to patients.">SMS history</button>
                        <button onClick={() => goTo("sms")} style={navBtn("sms")} title="SMS dev — internal SMS testing tools.">SMS dev</button>
                        <button onClick={() => goTo("staff_dev")} style={navBtn("staff_dev")} title="Staff dev — internal staff-data tools.">STAFF dev</button>
                        <button onClick={() => goTo("pwa_dev")} style={navBtn("pwa_dev")} title="PWA dev — internal patient-app tools.">PWA dev</button>
                        <button onClick={() => goTo("sms_queue")} style={navBtn("sms_queue")} title="SMS queue — pending/outbound SMS queue.">SMS queue</button>
                        <button onClick={() => goTo("sitedetails")} style={navBtn("sitedetails")} title="Site details — per-site configuration and metadata.">Site details</button>
                        {/* Invoice processing — the extraction pipeline and its
                            rules. Developer-facing: it reads every organisation's
                            invoices, so it lives here rather than under Stock. */}
                        <button onClick={() => goTo("invoice_processing")} style={navBtn("invoice_processing")} title="Invoice processing — monitor supplier invoice extraction stage by stage, and edit the rules that drive it.">Invoice processing</button>
                        <button onClick={() => goTo("site_management")} style={navBtn("site_management")} title="Site management — every organisation on the platform: members, login details, and access emails.">Site management</button>
                        {/* CQC Inspector view — every organisation's connected CQC
                            sites and compliance progress, read-only, centrally. */}
                        <button onClick={() => goTo("cqc_inspector")} style={navBtn("cqc_inspector")} title="CQC Inspector view — every organisation's connected CQC sites and compliance progress, in one place.">CQC Inspector view</button>
                      </div>}
                  </div>}

                {/* Dashboard panels — AI-assisted, user-composed dashboards */}
                

                {/* Spacer pushes Settings/Log out to the bottom */}
                <div style={{
              flexGrow: 1,
              minHeight: 20
            }}></div>

                {/* Clinical Record - reinstated, but only for organisations that
                    have switched the module on in
                    Settings -> Organisation Settings -> Modules.
                    The `clinical_record` route below is gated on the same flag. */}
                {clinicalRecordVisible && <button onClick={() => goTo("clinical_record")} style={navBtn("clinical_record")} title="Clinical record - consultations (free text or template-driven), QEAX components, patient records and template library. Sandbox.">
                    Clinical Record</button>}

                {/* Estate inspection — org-wide outlier snapshot. Sits with
                    Business Insights: both are whole-organisation analysis
                    rather than a module view. */}
                <button onClick={() => goTo("estate_inspection")} style={navBtn("estate_inspection")} title="Estate inspection — every branch measured against its peers across activity, deliveries, staffing, attendance, safety, SOPs, services and messaging, with a written summary of the outliers.">
                  Estate Inspection</button>

                

                  {/* Settings — now a single top-level button opening the
                      three-tabbed dialog directly (Organisation Settings /
                      Site Settings / Configurables). The old collapsible
                      "Settings" folder is gone: it shared a name with the
                      button inside it, and the Organisation page it held is
                      now the Configurables tab of this same dialog. The
                      `org_settings` route below is left intact. */}
                  <button onClick={() => {
              handleSettingsClick();
              setOpenMenu(null);
            }} style={navBtn(settingsOpen)} data-tour="nav-settings" title="Settings — organisation settings, site settings, and the financial configurables used by Business Insights.">
                    Settings</button>

                  {/* SOP Upload and Configurables were orphaned by the folder's
                      removal, so they sit at top level — Pharmace all only. */}
                  {(localStorage.getItem("organisationName") || "").trim() === "Pharmace all" && <>
                      {/* SOP Upload — DISABLED (button retained for later re-enable;
                          the sop_upload view render below is left intact). */}
                      <button disabled style={{
                ...navBtnStyle,
                opacity: 0.45,
                cursor: "not-allowed"
              }} title="SOP upload is currently disabled.">SOP Upload</button>
                      <button onClick={() => goTo("config")} style={navBtn("config")} title="Configurables — admin-editable values used across the platform.">Configurables</button>
                    </>}

                {/* Communications — the hub for every message the platform
                    sends. Now owns the Action centre (moved out of Head Office).
                    Settings still has a Communications TAB, which is the staff
                    notification settings slice of this same page. */}
                <button onClick={() => goTo("communications")} style={navBtn("communications")} data-tour="nav-communications" title="Communications — Action centre, staff notifications, rota circulations, appraisal emails, scheduled reports and patient messaging, gathered in one place.">
                  Communications</button>

                {/* Appraisal embedded demo — Pharmace all + Southtown Group only.
                    Right-click offers a reset of the remembered demo link. */}
                {appraisalDemoVisible && <button onClick={openAppraisalEmbeddedDemo} disabled={appraisalDemoBusy} onContextMenu={async e => {
              e.preventDefault();
              if (!localStorage.getItem(APPRAISAL_DEMO_LS)) {
                appAlert("No demo link is stored in this browser.");
                return;
              }
              if ((await appConfirm("Forget the stored demo link?\n\nThe next click will create a fresh demo key. The old key stays valid until revoked in Settings \u2192 Site Settings (label: 'Appraisal embedded demo')."))) {
                localStorage.removeItem(APPRAISAL_DEMO_LS);
              }
            }} style={{
              ...navBtnStyle,
              opacity: appraisalDemoBusy ? 0.6 : 1
            }} title="Opens a live example of the embeddable staff appraisal package \u2014 the standalone webpage a branch site would host, keyed to the Demo Live pharmacy. Right-click to reset the stored demo link.">
                    {appraisalDemoBusy ? "Appraisal embedded\u2026" : "Appraisal embedded"}
                  </button>}

                <button onClick={handleLogout} style={navBtnStyle} title="Log out of the management platform.">Log out</button>

                {/* ── Navigator ─────────────────────────────────────────────
                    Below the menu block, separated by a rule so it reads as a
                    tool rather than another destination — it changes nothing
                    about where you are. Toggles the floating panel, and says
                    which it will do, so a panel dragged behind something or
                    off to one side is still findable from here. */}
                <div style={{
              borderTop: "1px solid #F0F0F0",
              marginTop: 8,
              paddingTop: 8
            }}>
                  <button onClick={() => setNavigatorOpen(o => !o)} style={{
                ...navBtnStyle,
                backgroundColor: navigatorOpen ? "#E6F7FF" : "#F5F5F5",
                borderColor: navigatorOpen ? "#0E8CFF" : "#D9D9D9",
                color: navigatorOpen ? "#0958D9" : "#434343",
                fontWeight: navigatorOpen ? 700 : 400
              }} data-tour="nav-navigator" title="Opens a small floating panel that explains where you are and answers 'how do I…' questions. You can drag it out of the way and it stays open while you follow the steps.">
                    {navigatorOpen ? "Hide navigator" : "Show navigator"}
                  </button>

                  {/* Take the tour \u2014 replays the interactive spotlight
                      walkaround (GuidedTour.js). Grouped with Navigator since
                      neither one changes where you are, just what you know
                      about it. */}
                  <button onClick={() => setTourOpen(true)} style={{
                ...navBtnStyle,
                marginTop: 6
              }} title="Replay the interactive walkaround of the main screens.">
                    Take the tour
                  </button>
                </div>
              </div>

              {/* Right content area */}
              <div style={{
            flex: 1,
            minWidth: 0
          }}>

            {clarificationOptions && <div style={{
              marginTop: 20
            }}>
                <p>{clarificationOptions.message}</p>
                {clarificationOptions.missingFields.includes("pharmacy name") && <>
                    <label>Choose Pharmacy: </label>
                    <select value={selectedPharmacy} onChange={e => setSelectedPharmacy(e.target.value)}>
                      <option value="">--Select--</option>
                      <option value="Oxford Pharmacy">Oxford Pharmacy</option>
                      <option value="High Street Pharmacy">High Street Pharmacy</option>
                      <option value="City Centre Pharmacy">City Centre Pharmacy</option>
                    </select>
                  </>}
                {clarificationOptions.missingFields.includes("metric") && <>
                    <label> Choose Metric: </label>
                    <select value={selectedMetric} onChange={e => setSelectedMetric(e.target.value)}>
                      <option value="">--Select--</option>
                      <option value="totalCost">Total Cost</option>
                      <option value="count">Count</option>
                      <option value="totalPackets">Total Packets</option>
                      <option value="averagePrice">Average Price</option>
                      <option value="expiringSoon">Expiring Soon</option>
                    </select>
                  </>}
                <button onClick={handleClarifiedQuery} disabled={clarificationOptions.missingFields.includes("pharmacy name") && !selectedPharmacy || clarificationOptions.missingFields.includes("metric") && !selectedMetric} style={{
                marginLeft: "10px"
              }}>
                  Submit Clarified Query
                </button>
              </div>}

            {/* Keyed on view+epoch: every rail click remounts the target view
                fresh at its top level (see goTo). display:"contents" keeps the
                wrapper out of the flex layout. */}
            {inspectorOpen && <InspectorLauncher token={token} userData={userData} onClose={() => setInspectorOpen(false)} />}
            <div key={`${currentView}-${navEpoch}`} style={{
              display: "contents"
            }}>
            {isCareViewAllowed("mockup") && currentView === "mockup" && <Mockup userData={userData} />}
            {currentView === "head_office" && <HeadOffice token={token} onNavigateApp={setCurrentView} onOpenSettings={() => setSettingsOpen(true)} />}
            {currentView === "single_pharmacy" && <SinglePharmacy token={token} goTo={goTo} />}
            {currentView === "message_config" && <MessageConfiguration token={token} />}
            {currentView === "single_staff" && <SingleStaff token={token} userData={userData} />}
            {currentView === "staff" && <Staff token={token} userData={userData} />}
            {currentView === "holiday_rota" && <HolidayRota token={token} userData={userData} />}
            {currentView === "workforce" && <Workforce token={token} userData={userData} />}
            {currentView === "pharmacist_rota" && <PharmacistRota />}
            {currentView === "inspection" && <Inspection token={token} userData={userData} />}
            
            {isCareViewAllowed("regulatory_compliance") && currentView === "regulatory_compliance" && <RegulatoryCompliance token={token} userData={userData} />}
            {currentView === "cqc_compliance" && <CqcSites token={token} userData={userData} />}
            {currentView === "cqc_worklist" && <CqcWorklist token={token} userData={userData} />}
            {currentView === "care_inspector" && ((localStorage.getItem("organisationName") || "").trim() === "Pharmace all" ? <CqcInspectorView isPlatformAdmin={true} /> : <CqcWorkspace readOnly />)}
            {currentView === "cqc_inspector" && <CqcInspectorView isPlatformAdmin={(localStorage.getItem("organisationName") || "").trim() === "Pharmace all"} />}
            {currentView === "config" && <Config token={token} />}
            {currentView === "sop_upload" && <SOPUpload />}
            {currentView === "qmap" && <QMap userData={userData} />}
            {isCareViewAllowed("stock_financials") && currentView === "stock_financials" && <StockFinancials userData={userData} onOpenPictorial={() => goTo("shelfPictorialView")} />}
            {/* No menu entry of its own - reached from the Scanned items screen
                and returned from by the button in its header. */}
            {isCareViewAllowed("shelfPictorialView") && currentView === "shelfPictorialView" && <ShelfPictorialView userData={userData} onOpenFinancials={() => goTo("stock_financials")} />}
            {isCareViewAllowed("stock_graphs") && currentView === "stock_graphs" && <StockGraphs userData={userData} />}
            {/* No menu entry of its own - "Analysis and transfer" lands on the
                overview below, and this is reached from its Detailed button. */}
            {isCareViewAllowed("stock_excess") && currentView === "stock_excess" && <StockExcess userData={userData} onOpenOverview={() => goTo("warehouse_view")} />}
            {isCareViewAllowed("warehouse_view") && currentView === "warehouse_view" && <WarehouseView userData={userData} onOpenDetail={() => goTo("stock_excess")} />}
            {isCareViewAllowed("stock_invoices") && currentView === "stock_invoices" && <StockInvoices userData={userData} />}
            {currentView === "pickups" && <Pickups userData={userData} />}
            {currentView === "stafflogin" && <Stafflogin userData={userData} />}
            {currentView === "smsprod" && <SmsProd userData={userData} />}
            {currentView === "sms" && <Sms userData={userData} />}
            {currentView === "sms_queue" && <SMSQueue userData={userData} />}
            {currentView === "staff_dev" && <StaffDev userData={userData} />}
            {currentView === "pwa_dev" && <PwaDev userData={userData} />}
            {NAV_GROUPS.delivery.includes(currentView) && <DeliveryHub tab={currentView} onTab={v => goTo(v)}>
                {currentView === "route_analysis" && <RouteAnalysis userData={userData} initialPharmacyName={navParams?.pharmacyName} />}
                {currentView === "delivery_activity" && <DeliveryActivity userData={userData} token={token} />}
                {currentView === "payments" && <Payments userData={userData} token={token} />}
              </DeliveryHub>}

            {isCareViewAllowed("services") && currentView === "services" && <Services userData={userData} />} {/* <-- ADDED */}
      {isCareViewAllowed("nms") && currentView === "nms" && <Nms userData={userData} />} {/* <-- ADDED */}
            {isCareViewAllowed("appts_analysis") && currentView === "appts_analysis" && <ApptsAnalysis userData={userData} />}
            {currentView === "epos" && <Epos userData={userData} />}
            {currentView === "sitedetails" && <Sitedetails userData={userData} />}
            {currentView === "updates" && <Updates userData={userData} />}
            {isCareViewAllowed("business_insights") && currentView === "business_insights" && <BusinessInsights />}
            {isCareViewAllowed("business_insights") && currentView === "business_insights" && biNotice && <div role="dialog" aria-modal="true" aria-labelledby="bi-notice-title" onClick={() => setBiNotice(false)} style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.35)",
                zIndex: 2000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16
              }}>
                <div onClick={e => e.stopPropagation()} style={{
                  background: "#fff",
                  borderRadius: 10,
                  maxWidth: 520,
                  width: "100%",
                  padding: "20px 22px",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
                  borderTop: "5px solid #FA8C16"
                }}>
                  <div id="bi-notice-title" style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#D46B08",
                    marginBottom: 10
                  }}>🚧 Under construction</div>
                  <p style={{
                    fontSize: 14,
                    lineHeight: 1.55,
                    color: "#262626",
                    margin: "0 0 10px"
                  }}>
                    Business insights is under construction. It is designed to give a financial picture of the organisation.</p>
                  <p style={{
                    fontSize: 14,
                    lineHeight: 1.55,
                    color: "#262626",
                    margin: "0 0 16px"
                  }}>
                    In order to be of use it requires additional data sources not yet enabled — this gives an example of what could be achieved.</p>
                  <div style={{
                    textAlign: "right"
                  }}>
                    <button autoFocus onClick={() => setBiNotice(false)} style={{
                      background: "#FA8C16",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      padding: "8px 18px",
                      fontWeight: 600,
                      cursor: "pointer"
                    }}>Close</button>
                  </div>
                </div>
              </div>}
            {currentView === "estate_inspection" && <EstateInspection />}
            {isCareViewAllowed("dashpanels") && currentView === "dashpanels" && <DashboardPanels />}
            {currentView === "clinical_record" && clinicalRecordVisible && <ClinicalRecord />}
            {currentView === "org_settings" && <OrgSettings />}
            {currentView === "communications" && <Communications goTo={goTo} />}
            {currentView === "site_management" && <SiteManagement />}
            {currentView === "invoice_processing" && <InvoiceProcessing />}
            </div>
              </div>
            </div>

            <Shelves open={shelvesOpen} onClose={() => setShelvesOpen(false)} />
            {/* Closing Settings re-reads the optional-module flags, so a toggle
                saved in the dialog takes effect on the rail immediately. */}
            <Settings open={settingsOpen} onClose={() => {
          setSettingsOpen(false);
          setClinicalRecordVisible(readClinicalRecordEnabled());
        }} />

            {/* Rendered here, outside the layout flex and after the dialogs, so
                its fixed positioning is relative to the viewport and its stacking
                context is the document root — "always on top" only holds if it is
                not nested inside something with its own transform or z-index.
                Unmounted rather than hidden when closed: reopening should start
                from where you are now, not from a stale question. */}
            {navigatorOpen && <Navigator currentView={currentView} goTo={goTo} onClose={() => setNavigatorOpen(false)} />}

            {/* Guided tour — same "rendered outside the layout flex, always on
                top" reasoning as Navigator above. Unmounted rather than
                hidden when closed. */}
            {tourOpen && <GuidedTour goTo={goTo} onFinish={closeTour} />}

            {showDialog && <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          zIndex: 1000
        }}>
                <div style={{
            background: "white",
            padding: "20px",
            borderRadius: "8px",
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflowY: "auto",
            marginTop: "30px"
          }}>
                  <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px"
            }}>
                    <h3 style={{
                margin: 0
              }}>AI Response - </h3>
                    <div>
                      <button onClick={handleExportToExcel} style={{
                  marginRight: "10px"
                }}>Export to Excel</button>
                      <button onClick={() => setShowDialog(false)}>Close</button>
                    </div>
                  </div>

                  {Array.isArray(answer) && <>
                      <input type="text" placeholder="Search..." value={filterText} onChange={e => {
                setFilterText(e.target.value);
                setCurrentPage(1);
              }} style={{
                marginBottom: "10px",
                padding: "5px",
                width: "100%"
              }} />
                      <div style={{
                overflowX: "auto"
              }}>
                        <table style={{
                  borderCollapse: "collapse",
                  width: "100%"
                }}>
                          <thead>
                            <tr>
                              {Object.keys(answer[0] || {}).filter(k => !["_id", "id", "log"].includes(k)).map(key => <th key={key} onClick={() => {
                        if (sortKey === key) {
                          setSortAsc(!sortAsc);
                        } else {
                          setSortKey(key);
                          setSortAsc(true);
                        }
                      }} style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        backgroundColor: "#f2f2f2",
                        textTransform: "capitalize",
                        cursor: "pointer"
                      }}>
                                  {key} {sortKey === key ? sortAsc ? "↑" : "↓" : ""}
                                </th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedAnswer.map((item, index) => <tr key={index}>
                                {Object.entries(item).filter(([k]) => !["_id", "id", "log"].includes(k)).map(([key, value]) => {
                        let displayValue = value;
                        if (["price", "value", "totalPrice", "totalValue"].includes(key) && typeof value === "number") {
                          displayValue = `£${(value / 100).toFixed(2)}`;
                        } else if (typeof value === "object") {
                          displayValue = JSON.stringify(value);
                        } else {
                          displayValue = String(value);
                        }
                        const isTruncatable = ["vmppNm", "amppNm"].includes(key) && displayValue.length > 25;
                        const cellText = isTruncatable ? displayValue.slice(0, 25) + "…" : displayValue;
                        return <td key={key} style={{
                          border: "1px solid #ddd",
                          padding: "8px",
                          maxWidth: "200px",
                          overflowWrap: "break-word"
                        }} title={isTruncatable ? displayValue : ""}>
                                      {cellText}
                                    </td>;
                      })}
                              </tr>)}
                          </tbody>
                        </table>
                      </div>
                      <div style={{
                marginTop: "10px",
                textAlign: "center"
              }}>
                        <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>Prev</button>
                        <span style={{
                  margin: "0 10px"
                }}>Page {currentPage} of {totalPages}</span>
                        <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>Next</button>
                      </div>
                    </>}

                  {!Array.isArray(answer) && <pre style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word"
            }}>
                      {loadingAnswer ? "Searching..." : answer}
                    </pre>}
                </div>
              </div>}
          </>}
      </div>
    </LocalizationProvider>;
};
export default App;
